import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaHome, FaBook, FaSpinner, FaArrowLeft } from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type HomeworkTask = {
  id: number;
  taskType: string;
  taskOrder: number;
  taskDescription: string;
  correctAnswer: string | null;
  audioReference: string | null;
  points: number;
};

type Homework = {
  id: number;
  level: string;
  chapterNumber: number;
  title: string;
  instructions: string | null;
  totalPoints: number;
  tasks: HomeworkTask[];
};

const taskTypeLabels: Record<string, string> = {
  BEISPIEL_SATZ: "Beispiel Satz (Example Sentences)",
  SATZBAU: "Satzbau (Sentence Construction)",
  ERGANZUNGEN: "Ergänzungen (Fill in the Blanks)",
  HOREN: "Hören (Listening)",
  SCHREIBEN: "Schreiben (Writing)",
};

const MentorHomeworkViewPage: React.FC = () => {
  const { homeworkId } = useParams<{ homeworkId: string }>();
  const navigate = useNavigate();

  const [homework, setHomework] = useState<Homework | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    fetchHomework();
  }, [homeworkId]);

  const fetchHomework = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/homework/${homeworkId}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setHomework(data);
      } else {
        setError("Failed to load homework");
      }
    } catch (err) {
      console.error("Error fetching homework:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  if (error || !homework) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">
            {error || "Homework not found"}
          </p>
          <button
            onClick={() => navigate("/mentor/homework")}
            className="px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
          >
            Back to Homework
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaBook />}
        title="View Homework"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Homework", to: "/mentor/homework" },
          { label: "View" },
        ]}
      />

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/mentor/homework")}
          className="mb-6 flex items-center gap-2 text-[#009fe3] hover:text-cyan-600 font-semibold transition-colors"
        >
          <FaArrowLeft />
          Back to Homework List
        </button>

        {/* Homework Info */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {homework.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <span className="text-sm font-semibold text-gray-700">
                Level:
              </span>
              <div className="mt-1">
                <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold bg-blue-100 text-blue-800">
                  {homework.level}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-700">
                Chapter:
              </span>
              <div className="mt-1">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 font-bold rounded-md">
                  {homework.chapterNumber}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-700">
                Total Points:
              </span>
              <div className="mt-1">
                <span className="text-2xl font-bold text-gray-900">
                  {homework.totalPoints}
                </span>
              </div>
            </div>
          </div>

          {homework.instructions && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-bold text-gray-900 mb-2">
                Instructions:
              </h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {homework.instructions}
              </p>
            </div>
          )}
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Tasks ({homework.tasks.length})
          </h2>

          <div className="space-y-6">
            {homework.tasks.map((task, index) => (
              <div
                key={task.id}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900">
                    Task {task.taskOrder}
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-800">
                      {taskTypeLabels[task.taskType] || task.taskType}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800">
                      {task.points} points
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Description:
                  </h4>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap bg-white p-3 rounded border border-gray-200">
                    {task.taskDescription}
                  </p>
                </div>

                {task.taskType === "HOREN" && (
                  <>
                    {task.audioReference && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Audio Reference:
                        </h4>
                        <p className="text-sm text-gray-800 bg-amber-50 p-3 rounded border border-amber-200">
                          {task.audioReference}
                        </p>
                      </div>
                    )}
                    {task.correctAnswer && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          Correct Answer:
                        </h4>
                        <p className="text-sm text-gray-800 bg-green-50 p-3 rounded border border-green-200">
                          {task.correctAnswer}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => navigate(`/mentor/homework/${homework.id}/assign`)}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-200"
          >
            <FaBook />
            Assign to My Batches
          </button>
          <button
            onClick={() => navigate("/mentor/homework")}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Back
          </button>
        </div>
      </main>
    </div>
  );
};

export default MentorHomeworkViewPage;
